import { useEffect, useRef, useState, useCallback } from "react";
import { logger } from "@app/services/logger";

export interface WebSocketMessage {
  type: string;
  channel?: string;
  timestamp?: string;
  data?: any;
}

export interface UseWebSocketOptions {
  url: string;
  onMessage?: (message: WebSocketMessage) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  onClose?: () => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  enabled?: boolean;
}

export function useWebSocket(options: UseWebSocketOptions) {
  const {
    url,
    onMessage,
    onError,
    onOpen,
    onClose,
    reconnectInterval = 3000,
    maxReconnectAttempts = 3,
    enabled = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const reconnectAttemptsRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isConnectingRef = useRef(false);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
          wsRef.current.close(1000, "Client disconnect");
        }
      } catch (error) {
        // Ignore errors when closing
      }
      wsRef.current = null;
    }

    setIsConnected(false);
    isConnectingRef.current = false;
  }, []);

  const connect = useCallback(() => {
    if (!enabledRef.current || isConnectingRef.current) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (!url || (!url.startsWith("ws://") && !url.startsWith("wss://"))) {
      logger.error(`Invalid WebSocket URL: ${url}. WebSocket connection disabled.`);
      return;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      logger.warn(`Max WebSocket reconnection attempts (${maxReconnectAttempts}) reached. WebSocket disabled.`);
      return;
    }

    isConnectingRef.current = true;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.info("WebSocket connected");
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        isConnectingRef.current = false;
        onOpen?.();

        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          if (message.type === "pong") {
            return;
          }
          onMessage?.(message);
        } catch (error) {
          logger.error("Error parsing WebSocket message", error instanceof Error ? error : new Error(String(error)));
        }
      };

      ws.onerror = (error) => {
        if (ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
          logger.error("WebSocket error", error instanceof Error ? error : new Error(String(error)));
          onError?.(error);
        }
        isConnectingRef.current = false;
      };

      ws.onclose = (event) => {
        logger.info(`WebSocket disconnected. Code: ${event.code}, Reason: ${event.reason || "none"}`);
        setIsConnected(false);
        isConnectingRef.current = false;
        onClose?.();

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        if (enabledRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = reconnectInterval * Math.pow(2, reconnectAttemptsRef.current - 1);
          logger.info(`Reconnecting WebSocket in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (enabledRef.current) {
              connect();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          logger.warn(`Max WebSocket reconnection attempts (${maxReconnectAttempts}) reached. WebSocket disabled.`);
        }
      };
    } catch (error) {
      logger.error("Error creating WebSocket connection", error instanceof Error ? error : new Error(String(error)));
      isConnectingRef.current = false;
    }
  }, [url, reconnectInterval, maxReconnectAttempts, onMessage, onError, onOpen, onClose]);

  useEffect(() => {
    if (enabled && !isConnectingRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
      connect();
    } else if (!enabled) {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, url]);

  return {
    isConnected,
    reconnectAttempts: reconnectAttemptsRef.current,
  };
}
